import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Activity, RefreshCw, Calendar } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDashboardData, useMonthlyChartData, useSP500Data } from '../hooks/useDashboardData';
import { useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { toast } from 'sonner';
import { format, subDays, subMonths, startOfYear } from 'date-fns';

const Dashboard = () => {
  const queryClient = useQueryClient();
  const [activityPage, setActivityPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [user, setUser] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Use React Query hooks for data fetching with automatic caching
  const { data: dashboardData, isLoading: isDashboardLoading, refetch: refetchDashboard } = useDashboardData(startDate, endDate);
  const { data: chartData = [], isLoading: isChartLoading } = useMonthlyChartData(startDate, endDate);
  const { data: sp500Data = [] } = useSP500Data();

  // Update allTransactions when dashboardData changes
  useEffect(() => {
    if (dashboardData?.recent_transactions) {
      setAllTransactions(dashboardData.recent_transactions);
      setActivityPage(1); // Reset page when data refreshes
    }
  }, [dashboardData]);

  // Derive state from React Query data
  const data = dashboardData;
  const loading = isDashboardLoading || isChartLoading;

  // fetchDashboard removed - React Query handles data fetching automatically

  const handleDateRangeChange = (update) => {
    setDateRange(update);
    // React Query will automatically refetch when startDate/endDate change
  };

  const applyPreset = (preset) => {
    let from, to;
    to = new Date();
    
    switch(preset) {
      case '7days':
        from = subDays(to, 7);
        break;
      case '30days':
        from = subDays(to, 30);
        break;
      case '3months':
        from = subMonths(to, 3);
        break;
      case '6months':
        from = subMonths(to, 6);
        break;
      case '1year':
        from = subMonths(to, 12);
        break;
      case 'ytd':
        from = startOfYear(to);
        break;
      case 'all':
        from = null;
        to = null;
        break;
      default:
        return;
    }
    
    setDateRange([from, to]);
    setShowDatePicker(false);
    // React Query will automatically refetch when startDate/endDate change
  };

  const loadMoreTransactions = async () => {
    setLoadingMore(true);
    try {
      const nextPage = activityPage + 1;
      const response = await api.get(`/transactions?page=${nextPage}&limit=${itemsPerPage}`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setAllTransactions([...allTransactions, ...response.data.data]);
        setActivityPage(nextPage);
        toast.success(`Loaded ${response.data.data.length} more transactions`);
      } else {
        toast.info('No more transactions to load');
      }
    } catch (error) {
      console.error('Load more error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load more transactions');
    } finally {
      setLoadingMore(false);
    }
  };

  // fetchMcaNews and fetchEconomicIndicators removed - React Query handles data fetching automatically

  // useEffect removed - React Query hooks handle data fetching automatically on component mount

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Indicator with Spinner */}
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Loading Dashboard...</h3>
            <p className="text-slate-500">Fetching your investment data and analytics</p>
          </div>
        </div>
        
        {/* Skeleton Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-slate-200 rounded w-32"></div>
            </div>
          ))}
        </div>
        
        {/* Skeleton Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-48 mb-4"></div>
          <div className="h-64 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  const netReceived = Number(data?.net_received) || 0;
  const deposits = Number(data?.deposits) || 0;
  const purchasePrice = Number(data?.purchase_price) || 0;
  const totalReturns = Number(data?.total_returns) || 0;
  const totalInvestments = Number(data?.total_investments) || 0;
  const activeInvestments = Number(data?.active_investments) || 0;
  const avgMonthlyReturnPct = Number(data?.avg_monthly_return_pct) || 0;
  const avgYearlyReturnPct = Number(data?.avg_yearly_return_pct) || 0;

  // Calculate max value for scaling bars - use net_rtr instead of net_received
  const maxValue = Math.max(...chartData.map(d => d.net_rtr || 0), 1);

  return (
    <div className="space-y-0" data-testid="dashboard">
      {/* Blue Header Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 -mx-6 -mt-8 px-6 pt-6 pb-12 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Hello {user?.first_name} {user?.last_name}, Welcome to the Investors Platform
            </h1>
            <p className="text-blue-100 text-base">
              Here is the summary of all your MCA Directed investments
            </p>
          </div>
          <div className="text-right">
            <div className="text-blue-100 text-sm mb-1">Investor Since</div>
            <div className="text-white text-xl font-semibold">
              {data?.first_investment_date ? format(new Date(data.first_investment_date), 'MMMM yyyy') : 
               user?.created_at ? format(new Date(user.created_at), 'MMMM yyyy') : 'January 2021'}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid - Overlapping the blue header */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 -mt-8 mb-4">
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-5 shadow-lg stat-card border-2 border-blue-400" data-testid="stat-total-account-value">
          <div className="text-sm text-blue-100 mb-1 flex items-center">
            Total Account Value
            <span className="ml-2 text-blue-200 cursor-help" title="Total amount received from all your investments (sum of 'Net Received' from investments table)">ⓘ</span>
          </div>
          <div className="text-3xl font-bold text-white">
            ${netReceived.toLocaleString()}
          </div>
          <div className="text-xs text-blue-100 mt-1">Cash received from all investments</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm stat-card" data-testid="stat-deposits-made">
          <div className="text-sm text-slate-600 mb-1 flex items-center">
            Deposits Made
            <span className="ml-2 text-slate-400 cursor-help" title="Total cash deposited into your investment account from all deposit transactions">ⓘ</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">
            ${deposits.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm stat-card" data-testid="stat-invested-capital">
          <div className="text-sm text-slate-600 mb-1 flex items-center">
            Invested Capital
            <span className="ml-2 text-slate-400 cursor-help" title="Sum of purchase prices across all your investments">ⓘ</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">
            ${purchasePrice.toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">{activeInvestments} active investments</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm stat-card" data-testid="stat-total-returns">
          <div className="text-sm text-slate-600 mb-1 flex items-center">
            Total Returns
            <span className="ml-2 text-slate-400 cursor-help" title="Your net profit: Total Account Value (Net Received) minus Deposits Made">ⓘ</span>
          </div>
          <div className={`text-2xl font-bold ${totalReturns >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalReturns >= 0 ? '+' : ''}${totalReturns.toLocaleString()}
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm stat-card" data-testid="stat-avg-monthly-returns">
          <div className="text-sm text-slate-600 mb-1 flex items-center">
            Avg Monthly Return %
            <span className="ml-2 text-slate-400 cursor-help" title="Average monthly return percentage: Overall ROI divided by months since first investment">ⓘ</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {avgMonthlyReturnPct > 0 ? '+' : ''}{avgMonthlyReturnPct.toFixed(2)}%
          </div>
          <div className="text-xs text-slate-500 mt-1">Per month average</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm stat-card" data-testid="stat-avg-yearly-returns">
          <div className="text-sm text-slate-600 mb-1 flex items-center">
            Avg Yearly Return %
            <span className="ml-2 text-slate-400 cursor-help" title="Average yearly return percentage: Overall ROI divided by years since first investment">ⓘ</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {avgYearlyReturnPct > 0 ? '+' : ''}{avgYearlyReturnPct.toFixed(2)}%
          </div>
          <div className="text-xs text-slate-500 mt-1">Per year average</div>
        </div>
      </div>

      {/* Chart and Tiles Section - Compact Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Chart - Takes 2 columns */}
        <div className="bg-white rounded-xl p-4 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <h2 className="text-base font-bold text-slate-900">Monthly Returns with Net Received</h2>
              <span className="ml-2 text-slate-400 cursor-help text-lg" title="Monthly Net Received showing cash returns received from your investments each month. Hover over bars to see exact amounts">ⓘ</span>
            </div>
          </div>
          <p className="text-xs text-slate-600 mt-1">Showing {chartData.length} months of data</p>
          {chartData && chartData.length > 0 ? (
            <div style={{ height: '220px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', height: '100%', gap: '4px', alignItems: 'flex-end', position: 'relative' }}>
                {/* Y-axis labels */}
                <div style={{ position: 'absolute', left: '0', top: '0', bottom: '30px', width: '50px' }}>
                  <div style={{ position: 'relative', height: '100%' }}>
                    <div style={{ position: 'absolute', top: '0', left: '0', fontSize: '11px', fontWeight: 'bold' }}>
                      ${(maxValue / 1000).toFixed(0)}k
                    </div>
                    <div style={{ position: 'absolute', top: '25%', left: '0', fontSize: '11px', fontWeight: 'bold' }}>
                      ${(maxValue * 0.75 / 1000).toFixed(0)}k
                    </div>
                    <div style={{ position: 'absolute', top: '50%', left: '0', fontSize: '11px', fontWeight: 'bold' }}>
                      ${(maxValue * 0.5 / 1000).toFixed(0)}k
                    </div>
                    <div style={{ position: 'absolute', top: '75%', left: '0', fontSize: '11px', fontWeight: 'bold' }}>
                      ${(maxValue * 0.25 / 1000).toFixed(0)}k
                    </div>
                    <div style={{ position: 'absolute', bottom: '0', left: '0', fontSize: '11px', fontWeight: 'bold' }}>
                      $0k
                    </div>
                  </div>
                </div>
                
                {/* Bars */}
                <div style={{ display: 'flex', height: 'calc(100% - 30px)', gap: '2px', alignItems: 'flex-end', marginLeft: '60px', flex: 1 }}>
                  {chartData.map((item, index) => {
                    const heightPercent = ((item.net_rtr || 0) / maxValue) * 100;
                    // Show minimum height for $0 bars so they're visible
                    const displayHeight = heightPercent > 0 ? `${heightPercent}%` : '3px';
                    const barColor = heightPercent > 0 ? '#2563eb' : '#cbd5e1'; // Gray for $0 bars
                    
                    return (
                      <div 
                        key={index}
                        style={{
                          flex: 1,
                          minWidth: '6px',
                          maxWidth: '30px',
                          height: displayHeight,
                          backgroundColor: barColor,
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#1e40af';
                          e.currentTarget.querySelector('.tooltip').style.display = 'block';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#2563eb';
                          e.currentTarget.querySelector('.tooltip').style.display = 'none';
                        }}
                        title={`${item.month}: $${(item.net_rtr || 0).toLocaleString()}`}
                      >
                        <div 
                          className="tooltip"
                          style={{
                            display: 'none',
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#1e293b',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            whiteSpace: 'nowrap',
                            fontSize: '11px',
                            zIndex: 1000,
                            marginBottom: '5px'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', color: '#93c5fd' }}>{item.month}</div>
                          <div style={{ color: '#86efac' }}>${(item.net_rtr || 0).toLocaleString()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* X-axis labels - show first, middle, and last months */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', marginLeft: '60px', fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>
                {chartData.length > 0 && (
                  <>
                    <span>{chartData[0].month}</span>
                    {chartData.length > 2 && <span>{chartData[Math.floor(chartData.length / 2)].month}</span>}
                    <span>{chartData[chartData.length - 1].month}</span>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              <p style={{ color: '#94a3b8' }}>Loading chart data...</p>
            </div>
          )}
        </div>

        {/* Tiles - Takes 1 column, stacked vertically */}
        <div className="space-y-3">
          {/* Tile 1: Overall ROI */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-xs text-slate-600 mb-1 flex items-center">
              Overall ROI
              <span className="ml-1 text-slate-400 cursor-help text-base" title="Return on Investment calculated as (Total Returns / Deposits Made) × 100">ⓘ</span>
            </div>
            <div className={`text-2xl font-bold ${totalReturns >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {deposits > 0 ? ((totalReturns / deposits) * 100).toFixed(2) : '0.00'}%
            </div>
            <div className="text-xs text-slate-500 mt-1">Based on deposits made</div>
          </div>

          {/* Tile 2: Total Investments Count */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-xs text-slate-600 mb-1 flex items-center">
              Total Investments
              <span className="ml-1 text-slate-400 cursor-help text-base" title="Total number of MCA investments in your portfolio. Active investments are currently generating returns">ⓘ</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{totalInvestments}</div>
            <div className="text-xs text-slate-500 mt-1">{activeInvestments} active</div>
          </div>
        </div>
      </div>

      {/* Activity Feed - Fixed Height with Scrolling */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-900 mb-2">Activity Feed</h2>
        <p className="text-sm text-slate-500 mb-4">History of all your account activities, including deposits and withdrawals</p>
        {allTransactions && allTransactions.length > 0 ? (
          <div className="space-y-3">
            <div className="h-[300px] overflow-y-auto space-y-3 pr-2" style={{ scrollbarWidth: 'thin' }}>
              {allTransactions.map((transaction) => {
              // Parse amount - handle both number and string formats
              const parseAmount = (amt) => {
                if (typeof amt === 'number') return amt;
                if (typeof amt === 'string') {
                  return parseFloat(amt.replace(/[$,]/g, '')) || 0;
                }
                return 0;
              };
              
              const amount = parseAmount(transaction.Amount || transaction.amount);
              const transactionType = transaction.Type || transaction.type;
              const transactionDate = new Date(transaction['Scheduled For (datetime)'] || transaction['Scheduled For'] || transaction.date);
              
              // Skip transaction if date is invalid
              if (isNaN(transactionDate.getTime())) {
                return null;
              }
              
              // Check if transaction is today
              const today = new Date();
              const isToday = transactionDate.toDateString() === today.toDateString();
              
              // Generate dynamic description based on type
              const getDescription = () => {
                const amountStr = `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const timeRef = isToday ? 'today' : `on ${format(transactionDate, 'MMM d, yyyy')}`;
                
                switch (transactionType) {
                  case 'Investor Remittance':
                  case 'Remittance':
                    return `You have received remittances ${timeRef} in the amount of ${amountStr}`;
                  case 'Deposit':
                    return `You made a deposit ${timeRef} in the amount of ${amountStr}`;
                  case 'Withdrawal':
                    return `You withdrew ${amountStr} from your account ${timeRef}`;
                  case 'Investment':
                    return `You invested ${amountStr} ${timeRef}`;
                  case 'Dividend':
                    return `You received a dividend of ${amountStr} ${timeRef}`;
                  case 'Interest':
                    return `You earned ${amountStr} in interest ${timeRef}`;
                  case 'Fee':
                    return `A fee of ${amountStr} was charged ${timeRef}`;
                  default:
                    return `${transactionType}: ${amountStr} ${timeRef}`;
                }
              };

              return (
                <div
                  key={transaction['Transaction ID'] || transaction.id}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-full ${
                      transactionType === 'Investor Remittance' || transactionType === 'Remittance' || transactionType === 'Dividend' || transactionType === 'Interest'
                        ? 'bg-green-100'
                        : transactionType === 'Deposit' || transactionType === 'Investment'
                        ? 'bg-blue-100'
                        : transactionType === 'Withdrawal' || transactionType === 'Fee'
                        ? 'bg-red-100'
                        : 'bg-slate-100'
                    }`}>
                      {transactionType === 'Investor Remittance' || transactionType === 'Remittance' ? (
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      ) : transactionType === 'Deposit' || transactionType === 'Investment' ? (
                        <DollarSign className="w-5 h-5 text-blue-600" />
                      ) : transactionType === 'Withdrawal' || transactionType === 'Fee' ? (
                        <TrendingDown className="w-5 h-5 text-red-600" />
                      ) : (
                        <Activity className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-slate-900 font-medium">{getDescription()}</p>
                      <p className="text-sm text-slate-500">{format(transactionDate, 'MMM d, yyyy \'at\' h:mm a')}</p>
                    </div>
                  </div>
                  <div className={`text-lg font-semibold ${
                    transactionType === 'Withdrawal' || transactionType === 'Fee' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {transactionType === 'Withdrawal' || transactionType === 'Fee' ? '-' : '+'}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              );
            })}
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={loadMoreTransactions}
                disabled={loadingMore}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
              <button
                onClick={() => window.location.href = '/dashboard/transactions'}
                className="flex-1 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                View All Activities
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No recent activities</p>
          </div>
        )}
      </div>

      {/* MCA News and Economic Indicators sections removed */}
    </div>
  );
};

export default Dashboard;